// @flow
import { createAction } from 'redux-actions';
import { format } from 'date-fns';

// Actions
import {
  setServices,
  setCustomerVehicle,
  updatePrivacyPreferences,
  setContactInfo,
  setDeclinedServices,
  setAutoRecallServices,
  setApptServicePoint
} from 'store/reducers/appointment/actions';
import { setAppFlow } from 'store/reducers/userInteractions/actions';
import { setMaintenanceServices } from 'store/reducers/maintenance/actions';
import { setRepairServices } from 'store/reducers/repair/actions';
import { setGlobalRepairServices } from 'store/reducers/globalRepair/actions';
import { setDeclinedServices as updateDeclinedServices } from 'store/reducers/declined/actions';

// Selectors
import { getMaintenanceServices } from 'store/reducers/maintenance/selectors';
import { getRepairServices } from 'store/reducers/repair/selectors';
import { getDeclinedServices } from 'store/reducers/declined/selectors';

// ActionTypes
import * as actionTypes from './actionTypes';

// Utils
import { isObjEmpty } from 'utils/varValidation';

// Constants
import { APP_FLOW } from 'constants/appFlow';

export const setQuotingAppointment = apptData => async (dispatch, getState) => {
  const {
    isDefault,
    vehicleId,
    mileageDt,
    metaVehicleMake,
    displayTrim,
    displayDriveType,
    displayEngineSize,
    displayEngineType,
    displayTransmissionType,
    regDate,
    ...vehicleData
  } = apptData.vehicle;

  const {
    contalDetail: {
      emailPrivacyPreference,
      textPrivacyPreference,
      emailAddress: { emailAddress: email },
      phoneNumbers
    },
    firstName,
    lastName,
    id
  } = apptData.customer;

  const contactInfo = {
    firstName,
    lastName,
    phone: phoneNumbers[0].digits || phoneNumbers[2].digits,
    email,
    mobilePhone: phoneNumbers[2].digits,
    id
  };
  dispatch(setContactInfo(contactInfo));

  const packageMileage =
    apptData.recommendedService && apptData.recommendedService.mileage;
  const vehicle = regDate
    ? { regDate, ...vehicleData }
    : {
        regDate: format(new Date(apptData.vehicle.year, 0, 1), 'YYYY-MM-DD'),
        ...vehicleData
      };

  if (packageMileage && packageMileage !== vehicle.mileage) {
    vehicle.mileage = packageMileage;
  }

  await dispatch(setCustomerVehicle(vehicle));

  const privacyPreferences = {
    emailPrivacyPreference,
    textPrivacyPreference
  };
  dispatch(updatePrivacyPreferences(privacyPreferences));

  const autoRecallServices = apptData.autoRecallServices;
  const repairServices = apptData.repairServices.concat(
    apptData.globalRepairServices
  );
  const services = apptData.maintenanceServices.concat(repairServices);
  const allServices = autoRecallServices
    ? services.concat(autoRecallServices)
    : services;

  dispatch(updateQuoteServices(allServices, 'maintenance'));
  dispatch(updateQuoteServices(allServices, 'repair'));
  dispatch(
    setGlobalRepairServices(apptData.globalRepairServices || [], 'globalRepair')
  );
  const { quoteWithDeclinedServices: updatedDeclinedServices } = dispatch(
    updateQuoteServices(apptData.declinedServices, 'declined')
  );

  const formattedServices = allServices.map(service => ({
    dmsOpcode: service.dmsOpcode,
    id: service.id,
    name: service.name,
    price: service.price,
    shopDuration: service.shopDuration,
    notes: service.serviceComments,
    serviceKind: service.serviceKind,
    quoteServiceId: service.quoteServiceId
  }));
  dispatch(setServices(formattedServices));
  dispatch(setDeclinedServices(updatedDeclinedServices));
  autoRecallServices && dispatch(setAutoRecallServices(autoRecallServices));
  const readOnlyServices = formattedServices.concat(updatedDeclinedServices);

  const hasMenuType =
    !isObjEmpty(apptData.recommendedService) &&
    apptData.recommendedService.menuType;

  if (hasMenuType) {
    const {
      menuType: { drivingCondition, ...menuType },
      ...servicePointData
    } = apptData.recommendedService;
    const selectedServices =
      menuType.services &&
      menuType.services.filter(service => {
        return service.selectByDefault === true;
      });
    const servicePoint = {
      menuType: {
        ...menuType,
        services: selectedServices
      },
      ...servicePointData,
      selected: true
    };
    dispatch(setApptServicePoint(servicePoint));
    dispatch(
      setReadOnlyPackage({ ...menuType, ...servicePointData, selected: true })
    );
  }

  let allReadOnlyServices = hasMenuType
    ? readOnlyServices.concat(apptData.recommendedService.menuType.services)
    : readOnlyServices;
  allReadOnlyServices =
    allReadOnlyServices &&
    allReadOnlyServices.map(service => {
      return {
        ...service,
        selected: true
      };
    });
  dispatch(setReadOnlyServices(allReadOnlyServices));

  dispatch(setAppFlow(APP_FLOW.FIND_ME));
};

export const updateQuoteServices = (allServices, serviceType) => (
  dispatch,
  getState
) => {
  let individualServices;
  const quoteWithDeclinedServices = [];
  if (serviceType === 'maintenance') {
    individualServices = getMaintenanceServices(getState());
  } else if (serviceType === 'repair') {
    individualServices = getRepairServices(getState());
  } else if (serviceType === 'declined') {
    individualServices = getDeclinedServices(getState());
  }

  const updatedServices = individualServices.map(originalService => {
    let isQuoteService;
    if (serviceType === 'declined') {
      isQuoteService = allServices.find(
        quoteService =>
          +originalService.operationItemId === +quoteService.operationItemId
      );
      if (isQuoteService) {
        quoteWithDeclinedServices.push({
          ...isQuoteService,
          parentPlaceholderId: originalService.parentPlaceholderId
        });
      }
    } else {
      isQuoteService = allServices.find(
        quoteService =>
          +originalService.id === +quoteService.id ||
          +originalService.id === +quoteService.serviceIds
      );
    }
    if (isQuoteService) {
      return {
        ...originalService,
        price: +isQuoteService.price || +isQuoteService.originalPrice,
        quoteServiceId: isQuoteService.quoteServiceId
      };
    } else {
      return originalService;
    }
  });
  if (serviceType === 'maintenance') {
    dispatch(setMaintenanceServices(updatedServices));
  } else if (serviceType === 'repair') {
    dispatch(setRepairServices(updatedServices));
  } else if (serviceType === 'declined') {
    dispatch(updateDeclinedServices(updatedServices));
  }

  return { updatedServices, quoteWithDeclinedServices };
};

export const resetQuoteAppt = createAction(
  actionTypes.RESET_QUOTE_APPT,
  resetAppt => resetAppt
);

export const setReadOnlyServices = createAction(
  actionTypes.SET_READ_ONLY_SERVICES,
  readOnlyServices => readOnlyServices
);

export const setReadOnlyPackage = createAction(
  actionTypes.SET_READ_ONLY_PACKAGE,
  readOnlyPackages => readOnlyPackages
);

export const setQuoteConfirmationId = createAction(
  actionTypes.SET_QUOTE_CONFIRMATION_ID,
  quoteConfirmationId => quoteConfirmationId
);

export const setIsQuoteAppt = createAction(
  actionTypes.SET_IS_QUOTE_APPT,
  isQuoteAppt => isQuoteAppt
);

export const setShowQuoteApptError = createAction(
  actionTypes.SHOW_ERROR,
  showError => showError
);



// WEBPACK FOOTER //
// ./src/store/reducers/quoteAppt/actions.js